import { z } from 'zod';
import { IsoDateSchema, IsoDateTimeSchema, UuidSchema } from './common.js';

export const ReportStatusSchema = z.enum([
  'queued',
  'pulling',
  'analyzing',
  'narrating',
  'rendering',
  'delivered',
  'failed',
]);
export type ReportStatus = z.infer<typeof ReportStatusSchema>;

export const ReportCadenceSchema = z.enum(['weekly', 'monthly', 'custom']);
export type ReportCadence = z.infer<typeof ReportCadenceSchema>;

export const RunReportRequestSchema = z.object({
  project_id: UuidSchema,
  period_start: IsoDateSchema,
  period_end: IsoDateSchema,
  cadence: ReportCadenceSchema.default('custom'),
});
export type RunReportRequest = z.infer<typeof RunReportRequestSchema>;

export const AnomalySchema = z.object({
  metric: z.string(),
  date: IsoDateSchema,
  observed: z.number(),
  expected: z.number(),
  z_score: z.number(),
  severity: z.enum(['critical', 'high', 'medium']),
});
export type Anomaly = z.infer<typeof AnomalySchema>;

export const RecommendationSchema = z.object({
  title: z.string(),
  rationale: z.string(),
  expected_impact: z.number(),
  ease: z.number(),
  score: z.number(),
});
export type Recommendation = z.infer<typeof RecommendationSchema>;

export const ReportSchema = z.object({
  id: UuidSchema,
  project_id: UuidSchema,
  period_start: IsoDateSchema,
  period_end: IsoDateSchema,
  cadence: ReportCadenceSchema,
  status: ReportStatusSchema,
  anomalies: z.array(AnomalySchema).nullable(),
  recommendations: z.array(RecommendationSchema).nullable(),
  delivered_at: IsoDateTimeSchema.nullable(),
  failed_at: IsoDateTimeSchema.nullable(),
  failure_reason: z.string().nullable(),
  created_at: IsoDateTimeSchema,
});
export type Report = z.infer<typeof ReportSchema>;

// ─── Monthly report VIEW (free-tier fallback) ───────────────────────────────
//
// The `/v1/projects/:projectId/reports/monthly?month=YYYY-MM` endpoint returns a
// structured, always-populated JSON view of the report month — even when neither
// GSC nor GA4 is connected. Every section carries a stable `kind` discriminator
// so clients can render section-by-section without knowing the composition rules.
//
// Sections whose data source is unavailable still appear (with `state: 'na'`)
// so the UI can render an in-card "Connect X" placeholder rather than hiding
// the surface.

/** ISO month string used by the endpoint query param + response. Format: YYYY-MM. */
export const IsoMonthSchema = z.string().regex(/^\d{4}-\d{2}$/, 'must be YYYY-MM');
export type IsoMonth = z.infer<typeof IsoMonthSchema>;

export const RankGainerLoserSchema = z.object({
  tracked_keyword_id: UuidSchema,
  keyword: z.string(),
  first_position: z.number().int().nullable(),
  last_position: z.number().int().nullable(),
  delta: z.number().int(),
});
export type RankGainerLoser = z.infer<typeof RankGainerLoserSchema>;

export const RankMovementSectionSchema = z.object({
  kind: z.literal('rank_movement'),
  state: z.enum(['populated', 'empty', 'na']),
  tracked_keyword_count: z.number().int(),
  gainers: z.array(RankGainerLoserSchema),
  losers: z.array(RankGainerLoserSchema),
  avg_position_delta: z.number().nullable(),
  source: z.literal('serper').or(z.literal('rank_tracker')),
  empty_reason: z.string().nullable(),
});
export type RankMovementSection = z.infer<typeof RankMovementSectionSchema>;

export const RecurringIssueSchema = z.object({
  check_id: z.string(),
  count: z.number().int(),
});
export type RecurringIssue = z.infer<typeof RecurringIssueSchema>;

export const TechnicalHealthSectionSchema = z.object({
  kind: z.literal('technical_health'),
  state: z.enum(['populated', 'empty', 'na']),
  latest_health_score: z.number().int().nullable(),
  latest_audit_at: IsoDateTimeSchema.nullable(),
  criticals_opened: z.number().int(),
  criticals_closed: z.number().int(),
  recurring_issues: z.array(RecurringIssueSchema),
  empty_reason: z.string().nullable(),
});
export type TechnicalHealthSection = z.infer<typeof TechnicalHealthSectionSchema>;

export const DiscoveryClusterSchema = z.object({
  cluster_id: UuidSchema,
  label: z.string(),
  size: z.number().int(),
});
export type DiscoveryCluster = z.infer<typeof DiscoveryClusterSchema>;

export const DiscoverySectionSchema = z.object({
  kind: z.literal('discovery'),
  state: z.enum(['populated', 'empty', 'na']),
  new_keyword_count: z.number().int(),
  top_clusters: z.array(DiscoveryClusterSchema),
  intent_breakdown: z.record(z.string(), z.number().int()),
  empty_reason: z.string().nullable(),
});
export type DiscoverySection = z.infer<typeof DiscoverySectionSchema>;

export const BingWebmasterSectionSchema = z.object({
  kind: z.literal('bing_webmaster'),
  state: z.enum(['populated', 'empty', 'na']),
  total_impressions: z.number().int(),
  empty_reason: z.string().nullable(),
});
export type BingWebmasterSection = z.infer<typeof BingWebmasterSectionSchema>;

/**
 * Discriminated union over all monthly-view sections. Consumers should branch
 * on `section.kind`.
 */
export const MonthlyReportSectionSchema = z.discriminatedUnion('kind', [
  RankMovementSectionSchema,
  TechnicalHealthSectionSchema,
  DiscoverySectionSchema,
  BingWebmasterSectionSchema,
]);
export type MonthlyReportSection = z.infer<typeof MonthlyReportSectionSchema>;

export const IntegrationStatusSchema = z.object({
  gsc: z.boolean(),
  ga4: z.boolean(),
  bing_webmaster: z.boolean(),
});
export type IntegrationStatus = z.infer<typeof IntegrationStatusSchema>;

export const ConnectPromptSchema = z.object({
  message: z.string(),
  connect_url: z.string(),
});
export type ConnectPrompt = z.infer<typeof ConnectPromptSchema>;

export const MonthlyReportViewSchema = z.object({
  project_id: UuidSchema,
  month: IsoMonthSchema,
  period_start: IsoDateSchema,
  period_end: IsoDateSchema,
  mode: z.enum(['full', 'partial', 'fallback']),
  integrations: IntegrationStatusSchema,
  connect_prompts: z.array(ConnectPromptSchema),
  sections: z.object({
    rank_movement: RankMovementSectionSchema,
    technical_health: TechnicalHealthSectionSchema,
    discovery: DiscoverySectionSchema,
    bing_webmaster: BingWebmasterSectionSchema.optional(),
  }),
});
export type MonthlyReportView = z.infer<typeof MonthlyReportViewSchema>;
